import type { IssueLinkArgs, ProjectLinkArgs } from "@productlane/zero/types"
import { convertHtmlToText } from "@productlane/lib/src/convert-html"
import { splitEmailChunks } from "@productlane/lib/src/html"

import type {
	IssueSelectIssue,
	ProjectSelectProject,
} from "../components/right-bar/issue-select"
import { emailBody } from "@/lib/email-body"

interface BodySource {
	text: string
	origin: string
	firstInboundEmail?: object | null
	latestOutboundEmail?: object | null
	firstSlackMessage?: { html?: string | null } | null
	latestSlackMessage?: { html?: string | null } | null
}

/** What a linked issue or project quotes from the thread: its note, else the opening message. */
export function customerNeedBody(source: BodySource): string {
	const convertedText = convertHtmlToText(source.text)
	if (source.origin === "SLACK_CONNECT") {
		return convertedText
			? convertedText
			: convertHtmlToText(
					source.firstSlackMessage?.html ||
						source.latestSlackMessage?.html ||
						"",
				)
	}
	const htmlContent =
		emailBody(source.firstInboundEmail).html ||
		emailBody(source.latestOutboundEmail).html
	const trimmedHtmlContent = splitEmailChunks(htmlContent || "")[0] ?? ""
	return convertedText ? convertedText : trimmedHtmlContent
}

interface LinkCompany {
	id: string
	name: string
	domains: string[]
	linearCustomerId?: string | null
}

interface LinkThread {
	id: string
	painLevel: string
	videoId?: string | null
	company?: LinkCompany | null
}

interface Visibility {
	isVisible?: boolean
	notUpdateVisibility?: boolean
}

export function issueLinkArgs({
	thread,
	issue,
	body,
	newIssueId,
	visibility = {},
}: {
	thread: LinkThread
	issue: IssueSelectIssue
	body: string
	/** Id for the local row a Linear-only issue becomes. */
	newIssueId: string
	visibility?: Visibility
}): IssueLinkArgs {
	return {
		type: issue.type,
		feedbackId: thread.id,
		customerNeed: {
			body,
			priority: Number(thread.painLevel === "HIGH"),
			videoId: thread.videoId,
			company: thread.company
				? {
						id: thread.company.id,
						name: thread.company.name,
						domains: thread.company.domains,
						linearCustomerId: thread.company.linearCustomerId,
					}
				: undefined,
		},
		issueId: issue.type === "local" ? issue.id : newIssueId,
		linearIssue: {
			id: issue.linearTicketId,
			identifier: issue.identifier,
			priorityLabel: issue.priority,
			projectId: issue.projectId,
			dueDate: issue.dueDate ?? null,
			isVisible: visibility.isVisible || issue.isVisible,
			state: {
				type: issue.status ?? "",
				name: issue.statusName ?? "",
				color: issue.statusColor ?? "",
			},
			team: {
				id: issue.team,
			},
			title: issue.title,
			url: issue.url,
			description: issue.description,
		},
		notUpdateVisibility: visibility.notUpdateVisibility,
	}
}

export function projectLinkArgs({
	thread,
	project,
	body,
	newProjectId,
	visibility = {},
}: {
	thread: LinkThread
	project: ProjectSelectProject
	body: string
	/** Id for the local row a Linear-only project becomes. */
	newProjectId: string
	visibility?: Visibility
}): ProjectLinkArgs {
	return {
		type: project.type,
		feedbackId: thread.id,
		customerNeed: {
			body,
			priority: Number(thread.painLevel === "HIGH"),
			videoId: thread.videoId,
			...(!!thread.company && { company: thread.company }),
		},
		projectId: project.type === "local" ? project.id : newProjectId,
		linearProject: {
			id:
				project.type === "local"
					? project.id
					: (project.linearProjectId ?? project.id),
			linearProjectId: project.linearProjectId ?? project.id,
			name: project.name,
			description: project.description,
			icon: project.icon,
			url: project.url || "",
			startDate: null,
			targetDate: project.targetDate ?? null,
			state: project.state,
			color: project.color,
			sortOrder: project.sortOrder ?? 0,
			progress: project.progress,
			isVisible: visibility.isVisible ?? project.isVisible,
		},
		isVisible: visibility.isVisible ?? project.isVisible,
		notUpdateVisibility: visibility.notUpdateVisibility,
		createCustomerNeed: true,
	}
}
